import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { requestYieldCallback, getViewParameterValue } from '@theme/utilities';

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets – The fieldset elements.
 */

export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  /** Prevent infinite loops when we auto-correct invalid combinations OR when gallery drives variant */
  #isAutoFixing = false;

  /** IntersectionObserver for media -> variant */
  #mediaObserver;

  /** Track visibility per media id */
  #mediaVisibility = new Map();

  /** Debounce applying visible media */
  #mediaApplyT;

  /** Cache product JSON fetch */
  #productJsonPromise;

  connectedCallback() {
    super.connectedCallback();
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios.push(radios);

      const initialCheckedIndex = radios.findIndex((radio) => radio.dataset.currentChecked === 'true');
      if (initialCheckedIndex !== -1) {
        this.#checkedIndices.push([initialCheckedIndex]);
      }
    });

    this.addEventListener('change', this.variantChanged.bind(this));

    // ✅ NEW: Make carousel swipe change variant (robust: uses IntersectionObserver + /products/<handle>.js)
    this.#bindMediaVisibilityToVariant();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#mediaObserver) this.#mediaObserver.disconnect();
    if (this.#mediaApplyT) clearTimeout(this.#mediaApplyT);
  }

  /**
   * Handles the variant change event.
   * @param {Event} event
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    this.updateSelectedOption(event.target);
    this.dispatchEvent(new VariantSelectedEvent({ id: selectedOption.dataset.optionValueId ?? '' }));

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;

    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), loadsNewProduct);

    const url = new URL(window.location.href);
    const variantId = selectedOption.dataset.variantId || null;

    if (isOnProductPage) {
      if (variantId) url.searchParams.set('variant', variantId);
      else url.searchParams.delete('variant');
    }

    if (loadsNewProduct) url.pathname = newUrl;

    if (url.href !== window.location.href) {
      requestYieldCallback(() => history.replaceState({}, '', url.toString()));
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);
      if (!targetElement) throw new Error('Target element not found');
      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) radios[currentIndex].dataset.previousChecked = 'false';
          if (previousIndex !== undefined && radios[previousIndex]) radios[previousIndex].dataset.previousChecked = 'false';

          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          const newCurrentIndex = checkedIndices[0];
          const newPreviousIndex = checkedIndices[1];

          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
            fieldset.style.setProperty('--pill-width-current', `${radios[newCurrentIndex].parentElement?.offsetWidth || 0}px`);
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
            fieldset.style.setProperty('--pill-width-previous', `${radios[newPreviousIndex].parentElement?.offsetWidth || 0}px`);
          }
        }
      }
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);
      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) option.removeAttribute('selected');
      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption
   * @param {string | null} [source]
   * @param {string[]} [sourceSelectedOptionsValues]
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];
    const viewParamValue = getViewParameterValue();
    if (viewParamValue) params.push(`view=${viewParamValue}`);

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      else params.push(`option_values=${selectedOption.dataset.optionValueId}`);
    }

    if (this.closest('quick-add-component') || this.closest('swatches-variant-picker-component')) {
      if (productUrl?.includes('?')) productUrl = productUrl.split('?')[0];
      return `${productUrl}?section_id=section-rendering-product-card&${params.join('&')}`;
    }
    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Finds a valid selected option element (one that has a data-variant-id).
   * @returns {HTMLOptionElement | HTMLInputElement | null}
   */
  #findFirstValidSelectable() {
    const current = this.querySelector(
      'select option[selected][data-variant-id], fieldset input:checked[data-variant-id]'
    );
    if (current instanceof HTMLOptionElement || current instanceof HTMLInputElement) return current;

    const radio = this.querySelector('fieldset input[data-variant-id]:not([disabled])');
    if (radio instanceof HTMLInputElement) return radio;

    const opt = this.querySelector('select option[data-variant-id]:not([disabled])');
    if (opt instanceof HTMLOptionElement) return opt;

    return null;
  }

  /**
   * Applies a valid option element and triggers change once.
   * @param {HTMLOptionElement | HTMLInputElement} el
   */
  #applyValidSelection(el) {
    if (this.#isAutoFixing) return;
    this.#isAutoFixing = true;

    try {
      if (el instanceof HTMLInputElement) {
        el.checked = true;
        this.updateSelectedOption(el);
        el.dispatchEvent(new Event('change', { bubbles: true }));
        return;
      }

      if (el instanceof HTMLOptionElement) {
        const select = el.parentElement;
        if (!(select instanceof HTMLSelectElement)) return;

        select.value = el.value;
        this.updateSelectedOption(select);
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
    } finally {
      setTimeout(() => (this.#isAutoFixing = false), 0);
    }
  }

  /**
   * ✅ NEW: Bind visible .product-media slide -> variant (featured_media match).
   * Uses IntersectionObserver so it works with transform-based carousels too.
   */
  #bindMediaVisibilityToVariant() {
    const isOnProductPage = this.dataset.templateProductMatch === 'true' && !this.closest('product-card');
    if (!isOnProductPage) return;

    const scope =
      this.closest('.shopify-section') ||
      document.querySelector('main') ||
      document;

    const mediaEls = Array.from(scope.querySelectorAll('.product-media[data-media-id]'));
    if (mediaEls.length < 2) return; // no carousel

    // Observe visibility in viewport (root=null). This works even if the carousel uses transforms.
    this.#mediaObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const el = /** @type {HTMLElement} */ (entry.target);
          const raw = el.getAttribute('data-media-id');
          const id = raw ? Number(raw) : NaN;
          if (!Number.isFinite(id)) continue;
          this.#mediaVisibility.set(id, entry.intersectionRatio || 0);
        }

        clearTimeout(this.#mediaApplyT);
        this.#mediaApplyT = setTimeout(() => this.#applyMostVisibleMedia(), 120);
      },
      {
        root: null,
        threshold: [0, 0.15, 0.3, 0.5, 0.65, 0.8, 1],
      }
    );

    mediaEls.forEach((el) => this.#mediaObserver.observe(el));
  }

  async #applyMostVisibleMedia() {
    if (this.#isAutoFixing) return;

    let bestId = null;
    let bestRatio = 0;

    for (const [id, ratio] of this.#mediaVisibility.entries()) {
      if (ratio > bestRatio) {
        bestRatio = ratio;
        bestId = id;
      }
    }

    // Require a decent visibility so we don't flip variants mid-swipe
    if (!bestId || bestRatio < 0.55) return;

    const variantId = await this.#findVariantIdByFeaturedMedia(bestId);
    if (!variantId) return;

    this.#selectVariantById(variantId);
  }

  /**
   * Fetch product JSON reliably from /products/<handle>.js (Shopify standard endpoint).
   * @returns {Promise<any|null>}
   */
  #getProductJson() {
    if (this.#productJsonPromise) return this.#productJsonPromise;

    // Get handle from productUrl (best), else from location.
    const urlStr = this.dataset.productUrl || window.location.pathname;
    let handle = null;

    try {
      const u = new URL(urlStr, window.location.origin);
      const parts = u.pathname.split('/').filter(Boolean);
      const idx = parts.indexOf('products');
      if (idx !== -1 && parts[idx + 1]) handle = parts[idx + 1];
    } catch {
      // ignore
    }

    if (!handle) {
      const parts = window.location.pathname.split('/').filter(Boolean);
      const idx = parts.indexOf('products');
      if (idx !== -1 && parts[idx + 1]) handle = parts[idx + 1];
    }

    if (!handle) {
      this.#productJsonPromise = Promise.resolve(null);
      return this.#productJsonPromise;
    }

    this.#productJsonPromise = fetch(`/products/${handle}.js`, { credentials: 'same-origin' })
      .then((r) => (r.ok ? r.json() : null))
      .catch(() => null);

    return this.#productJsonPromise;
  }

  /**
   * Map featured_media.id -> variant.id
   * @param {number} mediaId
   * @returns {Promise<string|null>}
   */
  async #findVariantIdByFeaturedMedia(mediaId) {
    const product = await this.#getProductJson();
    if (!product?.variants?.length) return null;

    // /products/<handle>.js returns variants with "featured_media" in many themes,
    // but sometimes it uses "featured_image". We handle both.
    const v =
      product.variants.find((x) => Number(x?.featured_media?.id) === Number(mediaId)) ||
      product.variants.find((x) => Number(x?.featured_image?.id) === Number(mediaId));

    return v?.id ? String(v.id) : null;
  }

  /**
   * Select a variant in the UI by data-variant-id and trigger normal change flow.
   * @param {string} variantId
   */
  #selectVariantById(variantId) {
    if (!variantId || this.#isAutoFixing) return;

    const opt =
      this.querySelector(`select option[data-variant-id="${variantId}"]`) ||
      this.querySelector(`fieldset input[data-variant-id="${variantId}"]`);

    if (!opt) return;

    this.#isAutoFixing = true;
    try {
      if (opt instanceof HTMLOptionElement) {
        const sel = opt.parentElement;
        if (sel instanceof HTMLSelectElement) {
          sel.value = opt.value;
          this.updateSelectedOption(sel);
          sel.dispatchEvent(new Event('change', { bubbles: true }));
        }
        return;
      }

      if (opt instanceof HTMLInputElement) {
        opt.checked = true;
        this.updateSelectedOption(opt);
        opt.dispatchEvent(new Event('change', { bubbles: true }));
      }
    } finally {
      setTimeout(() => (this.#isAutoFixing = false), 0);
    }
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl
   * @param {boolean} shouldMorphMain
   */
  fetchUpdatedSection(requestUrl, shouldMorphMain = false) {
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) return;

        if (shouldMorphMain) {
          this.updateMain(html);
        } else {
          const newProduct = this.updateVariantPicker(html);

          // ✅ invalid-combo auto-fix stays
          const selected = this.selectedOption;
          const hasVariantId =
            (selected instanceof HTMLOptionElement || selected instanceof HTMLInputElement) &&
            !!selected.dataset.variantId;

          if (!hasVariantId) {
            const fallbackEl = this.#findFirstValidSelectable();
            if (fallbackEl) {
              this.#applyValidSelection(fallbackEl);
              return;
            }
          }

          if (this.selectedOptionId) {
            this.dispatchEvent(
              new VariantUpdateEvent(JSON.parse(textContent), this.selectedOptionId, {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
              })
            );
          }
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') console.warn('Fetch aborted by user');
        else console.error(error);
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());
    if (!newVariantPickerSource) throw new Error('No new variant picker source found');

    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource);
    return newProduct;
  }

  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');
    if (!main || !newMain) throw new Error('No new main source found');
    morph(main, newMain);
  }

  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');
    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) return undefined;
    return selectedOption;
  }

  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;
    if (!optionValueId) throw new Error('No option value ID found');
    return optionValueId;
  }

  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;
      if (!optionValueId) throw new Error('No option value ID found');
      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
